import type {Component, Editor} from 'grapesjs';
import type {ReactElement} from 'react';
import type {SaveActionType, SaveType} from '@FormMakerReact/lib/form-maker-storage-library';

export interface FieldMeta {
  component: string;
  order: number;
  label: string;
  format: string;
  customFieldIndex?: number;
  required?: boolean;
  /** Registry PK; populated by load/save, round-tripped opaquely. */
  idUserHasCustomField?: number;
}

export const PMW_CUSTOM_INPUT = 'postermywall-custom-input';

export const BIRTHDAY_FORMATS = {
  DMY: 'DD/MM/YYYY',
  MDY: 'MM/DD/YYYY',
  YMD: 'YYYY-MM-DD',
} as const;

export type BirthdayFormat = (typeof BIRTHDAY_FORMATS)[keyof typeof BIRTHDAY_FORMATS];

export const DEFAULT_FIELDS_META: readonly FieldMeta[] = [
  {component: 'postermywall-email-input', order: 1, label: 'pmwjs_email', format: 'email'},
  {component: 'postermywall-first-name-input', order: 2, label: 'pmwjs_first_name', format: 'string'},
  {component: 'postermywall-last-name-input', order: 3, label: 'pmwjs_last_name', format: 'string'},
  {component: 'postermywall-phone-input', order: 4, label: 'pmwjs_phone', format: 'tel'},
  {component: 'postermywall-birthday-input', order: 5, label: 'pmwjs_birthday', format: BIRTHDAY_FORMATS.DMY},
  {component: 'postermywall-address-input', order: 6, label: 'pmwjs_address', format: 'string'},
];

export interface FormMakerImmutableStore {
  idEmailSignupForm: string;
  name: string;
  idOwner: number;
  shareableUrl: string;
  isPublishedLink: boolean;
  isPublishedPopup: boolean;
  isPublishedEmbeddedForm: boolean;
  isAlreadyPublished: boolean;
  popupDelay: number;
  shortPopupDelay: number;
  longPopupDelay: number;
  popupHtmlContent: string;
  iframePopupHtmlContent: string;
  embeddedFormHtmlContent: string;
  isSharedInTeam: boolean;
  /** True when this session may save silently as it edits (a logged-in session). Anonymous drafts start false and flip to true once they adopt a form. */
  canAutosave: boolean;
  isEmbedded: boolean;
  nextBtnCaption: string;
}

export enum EditorViewport {
  MOBILE = 'mobile',
  TABLET = 'tablet',
  DESKTOP = 'desktop',
}

export enum CanvasDevice {
  DESKTOP = 'desktop',
  TABLET = 'tablet',
  MOBILE = 'mobile',
}

export enum FormMakerFormMode {
  SIGNUP = 'signup',
  CONFIRMATION = 'confirmation',
}

export const FORM_MODE_MODEL_KEY = 'formMakerFormMode';

export const FORM_MAKER_NAV_COMPACT_BREAKPOINT_PX = 1100;

export enum RightPanelView {
  DEFAULT = 'default',
  KEYBOARD_SHORTCUTS = 'keyboard-shortcuts',
}

export interface FormMakerState {
  hasUnsavedChanges: boolean;
  isSaving: boolean;
  saveFailed: boolean;
  saveErrorMessage: string | null;
  isEditorLoading: boolean;
  lastSavedAt: number | null;
  selectedComponent: Component | null;
  editorViewport: EditorViewport;
  canvasDevice: CanvasDevice;
  previewMode: boolean;
  fieldsMeta: FieldMeta[];
  formMode: FormMakerFormMode;
  rightPanelView: RightPanelView;
}

export interface SaveOptions {
  saveActionType?: SaveActionType;
  saveType?: SaveType;
}

export interface FormMakerActions {
  save: (opts?: SaveOptions) => Promise<boolean>;
  undo: () => void;
  redo: () => void;
  setPreviewMode: (next: boolean) => void;
  setCanvasDevice: (device: CanvasDevice) => void;
  selectComponent: (component: Component | null) => void;
  setFieldsMeta: (updater: (prev: FieldMeta[]) => FieldMeta[]) => void;
  setFormMode: (mode: FormMakerFormMode) => void;
  openRightPanelKeyboardShortcuts: () => void;
  closeRightPanelOverlay: () => void;
}

export interface FormMakerContextProps {
  editor: Editor | null;
  store: FormMakerImmutableStore;
  state: FormMakerState;
  actions: FormMakerActions;
}

export interface FormMakerProps {
  store: FormMakerImmutableStore;
}

export interface FormMakerComponentRegistryEntry {
  type: string;
  sectorTitle: string | (() => ReactElement);
  controls: readonly ((props: {component: Component}) => ReactElement | null)[];
}

export interface FormMakerBlockEntry {
  type: string;
  label: string;
  iconKey: string;
}
